import { Button } from "@medusajs/ui"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { ErrorState, PageHeader } from "@/components/ui"
import { fetchFreshStatus, useSystemStatus, type ErrorGroup, type JobRow, type PaymentHealth, type Problem, type RecentErrors, type SystemStatus, type Tone } from "@/lib/system-status"
import { Badge, LoadingCard } from "@/terabizz"

/**
 * Admin → System status: everything the IT team watches, on one screen that works on a phone at
 * 2 AM. Servers (ECS), database, Redis, traffic and payment errors, AWS alarms, the public
 * websites, background jobs, and the store's own signals (orders, Issues, emails, ShipStation,
 * renewals, recovery). Problems are listed first, worst first; refreshes every 30 seconds.
 * Backend: lib/system-status.ts.
 */

const TONE: Record<Tone, { badge: "success" | "attention" | "critical" | "neutral"; label: string; dot: string }> = {
  ok: { badge: "success", label: "Healthy", dot: "#29845a" },
  warn: { badge: "attention", label: "Needs attention", dot: "#b28400" },
  down: { badge: "critical", label: "Down", dot: "#c70a24" },
  unknown: { badge: "neutral", label: "No data", dot: "#8a8a8a" },
}

const ago = (iso: string | null | undefined, now: number) => {
  if (!iso) return "never"
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 48) return `${h} h ago`
  return `${Math.round(h / 24)} days ago`
}
const rounded = (n: number | null) => (n == null ? null : Math.round(n))
const pct = (n: number | null | undefined) => (n == null ? "—" : `${Math.round(n)}%`)
const num = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString())

function useNow(ms = 5000) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms)
    return () => clearInterval(t)
  }, [ms])
  return now
}

export function SystemStatusPage() {
  const { data, error, isLoading, isFetching } = useSystemStatus()
  const qc = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)
  const now = useNow()

  const refresh = async () => {
    setRefreshing(true)
    try {
      qc.setQueryData(["system-status"], await fetchFreshStatus())
    } catch {
      await qc.invalidateQueries({ queryKey: ["system-status"] })
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <>
      <PageHeader
        title="System status"
        subtitle={data ? `Checked ${ago(data.generated_at, now)} · refreshes every 30 seconds${data.env ? ` · ${data.env} (${data.region})` : ""}` : "Servers, database, payments, jobs and the store, in one place."}
        actions={
          <Button size="small" variant="secondary" onClick={refresh} isLoading={refreshing} disabled={isFetching && !refreshing}>
            Refresh
          </Button>
        }
      />
      {error && !data ? (
        <ErrorState error={error} />
      ) : isLoading || !data ? (
        <div className="flex flex-col gap-4">
          <LoadingCard lines={3} />
          <LoadingCard lines={6} />
        </div>
      ) : (
        <Dashboard s={data} now={now} />
      )}
    </>
  )
}

function Dashboard({ s, now }: { s: SystemStatus; now: number }) {
  const running = s.services.reduce((a, x) => a + x.running, 0)
  const desired = s.services.reduce((a, x) => a + x.desired, 0)
  const issues = s.store.issues
  return (
    <div className="flex flex-col gap-4">
      <Overall s={s} />

      {!s.aws.connected && (
        <div className="rounded-[12px] border border-[var(--tb-line)] bg-[var(--tb-surface,#fff)] px-4 py-3 text-[13px]">
          <p className="font-medium text-[var(--tb-ink)]">AWS checks aren't connected</p>
          <p className="mt-0.5 text-[var(--tb-ink-3)]">Servers, database, Redis, traffic and alarms can't be read yet. Store checks below still work.</p>
          {s.aws.error && <p className="mt-1 break-words font-mono text-[11px] text-[var(--tb-ink-3)]">{s.aws.error}</p>}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Servers running" value={s.services.length ? `${running} / ${desired}` : "—"} tone={worstOf(s.services.map((x) => x.tone))} />
        <Tile label="Failing requests (15 min)" value={s.traffic?.error_rate_15m != null ? `${s.traffic.error_rate_15m}%` : "—"} tone={s.traffic?.tone ?? "unknown"} hint={s.traffic?.requests_15m != null ? `${num(s.traffic.requests_15m)} requests` : undefined} />
        <Tile label="Orders (last hour)" value={num(s.store.orders_1h)} tone="ok" hint={s.store.last_order_at ? `Last ${ago(s.store.last_order_at, now)}` : undefined} />
        <Tile label="Open issues" value={issues ? num(issues.open) : "—"} tone={issues?.critical ? "down" : issues?.high ? "warn" : issues ? "ok" : "unknown"} hint={issues?.critical ? `${issues.critical} money` : undefined} to="/issues" />
      </div>

      <RecentErrorsCard r={s.recent_errors ?? null} failed={!!s.errors.recent_errors} now={now} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="Servers" tone={worstOf(s.services.map((x) => x.tone))}>
          {s.services.length ? (
            s.services.map((x) => (
              <Row
                key={x.name}
                tone={x.tone}
                title={x.label}
                sub={`${x.name}${x.deploy.revision ? ` · v${x.deploy.revision}` : ""}${x.restarting ? ` · restarting (deploy in progress, started ${ago(x.deploy.started_at, now)})` : x.deploy.in_progress ? " · deploying" : x.deploy.started_at ? ` · deployed ${ago(x.deploy.started_at, now)}` : ""}${x.deploy.state === "FAILED" ? " · last deploy FAILED" : ""}`}
                right={
                  <div className="flex items-center gap-x-3">
                    <Spark values={x.cpu_series} max={100} />
                    <div className="text-right">
                      <div className="tabular-nums text-[13px] text-[var(--tb-ink)]">
                        {x.running}/{x.desired}
                        {x.max != null ? <span className="text-[var(--tb-ink-3)]"> (max {x.max})</span> : null}
                      </div>
                      <div className="tabular-nums text-[11px] text-[var(--tb-ink-3)]">CPU {pct(x.cpu)} · Mem {pct(x.memory)}</div>
                    </div>
                  </div>
                }
              />
            ))
          ) : (
            <Empty>No server data.</Empty>
          )}
        </Card>

        <Card title="Database and Redis" tone={worstOf([s.database?.tone ?? "unknown", s.redis?.tone ?? "unknown"])}>
          {s.database ? (
            <Row
              tone={s.database.tone}
              title={`Database (${s.database.instance_class ?? "Postgres"})`}
              sub={`${s.database.status ?? "unknown"} · ${num(s.database.connections)} connections · ${s.database.free_storage_gb ?? "—"} GB disk free of ${s.database.allocated_gb ?? "—"} GB`}
              right={
                <div className="flex items-center gap-x-3">
                  <Spark values={s.database.cpu_series} max={100} />
                  <div className="text-right">
                    <div className="tabular-nums text-[13px]">CPU {pct(s.database.cpu)}</div>
                    <div className="tabular-nums text-[11px] text-[var(--tb-ink-3)]">{s.database.free_memory_gb ?? "—"} GB RAM free</div>
                  </div>
                </div>
              }
            />
          ) : (
            <Empty>No database data.</Empty>
          )}
          {s.redis ? (
            <Row
              tone={s.redis.tone}
              title={`Redis (${s.redis.node_type ?? "cache"})`}
              sub={`${s.redis.status ?? "unknown"} · ${num(s.redis.connections)} connections · job queues, checkout locks`}
              right={
                <div className="text-right">
                  <div className="tabular-nums text-[13px]">Memory {pct(s.redis.memory_pct)}</div>
                  <div className="tabular-nums text-[11px] text-[var(--tb-ink-3)]">CPU {pct(s.redis.cpu)}</div>
                </div>
              }
            />
          ) : null}
        </Card>

        <Card title="Traffic and payments (last hour)" tone={s.traffic?.tone ?? "unknown"}>
          {s.traffic ? (
            <>
              <Row
                tone={s.traffic.tone}
                title="API requests per minute"
                sub={`${num(s.traffic.errors_15m)} server errors in the last 15 min`}
                right={<div className="flex items-center gap-x-3"><Spark values={s.traffic.requests_series} /><span className="tabular-nums text-[13px]">{num(rounded(s.traffic.requests_series.at(-2) ?? null))}/min</span></div>}
              />
              {s.store.payments_1h ? (
                <PaymentRows p={s.store.payments_1h} problems={s.problems.filter((x) => x.area === "Payments")} />
              ) : (
                <Row tone={(s.traffic.declines_1h ?? 0) >= 30 ? "down" : "ok"} title="Card declines (Authorize.net)" sub="30+ in an hour usually means card testing" right={<span className="tabular-nums text-[13px]">{num(s.traffic.declines_1h)}</span>} />
              )}
            </>
          ) : (
            <Empty>No traffic data.</Empty>
          )}
        </Card>

        <Card title="Websites (checked from outside)" tone={worstOf(s.sites.map((x) => x.tone))}>
          {s.sites.length ? (
            s.sites.map((x) => (
              <Row
                key={x.id}
                tone={x.tone}
                title={x.label}
                sub={x.url}
                right={<span className="tabular-nums text-[13px]">{x.error ? x.error : `${x.status} · ${((x.ms ?? 0) / 1000).toFixed(2)} s`}</span>}
              />
            ))
          ) : (
            <Empty>No website addresses set on this server.</Empty>
          )}
        </Card>

        <Alarms alarms={s.alarms} now={now} />

        <Jobs jobs={s.jobs} now={now} />

        <Card title="Store" tone={worstOf([issues?.critical ? "down" : issues?.high ? "warn" : "ok", s.store.shipstation?.failed ? "warn" : "ok", s.store.renewals?.processing_stuck ? "down" : s.store.renewals?.overdue ? "warn" : "ok"])}>
          <Row tone="ok" title="Orders" sub={s.store.last_order_at ? `Last order ${ago(s.store.last_order_at, now)}` : "No orders in 30 days"} right={<span className="tabular-nums text-[13px]">{num(s.store.orders_1h)} last hour · {num(s.store.orders_24h)} today/24 h</span>} to="/orders" />
          {issues && (
            <Row
              tone={issues.critical ? "down" : issues.high ? "warn" : "ok"}
              title="Issues"
              sub={issues.latest[0] ? issues.latest[0].title : "Nothing needs attention"}
              right={<span className="tabular-nums text-[13px]">{issues.critical} money · {issues.high} stuck · {issues.normal} minor</span>}
              to="/issues"
            />
          )}
          {s.store.renewals && (
            <Row
              tone={s.store.renewals.processing_stuck ? "down" : s.store.renewals.overdue ? "warn" : "ok"}
              title="Subscription renewals today"
              sub={s.store.renewals.overdue || s.store.renewals.processing_stuck ? `${s.store.renewals.overdue} overdue · ${s.store.renewals.processing_stuck} stuck while charging` : "None overdue"}
              right={<span className="tabular-nums text-[13px]">{num(s.store.renewals.paid_today)} paid · {num(s.store.renewals.failed_today)} failed</span>}
            />
          )}
          {s.store.shipstation && (
            <Row
              tone={s.store.shipstation.failed || s.store.shipstation.pending_over_1h ? "warn" : "ok"}
              title="ShipStation"
              sub={`${s.store.shipstation.on_hold} on hold for review`}
              right={<span className="tabular-nums text-[13px]">{s.store.shipstation.failed} failed · {s.store.shipstation.pending_over_1h} waiting 1 h+</span>}
            />
          )}
          {s.store.messages_24h && (
            <Row
              tone={rateTone(s.store.messages_24h.email_failed, s.store.messages_24h.email_sent, 0.05, 20)}
              title="Emails and texts (24 h)"
              sub={`Texts: ${num(s.store.messages_24h.sms_sent)} sent · ${num(s.store.messages_24h.sms_failed)} failed`}
              right={<span className="tabular-nums text-[13px]">{num(s.store.messages_24h.email_sent)} emails · {num(s.store.messages_24h.email_failed)} failed</span>}
            />
          )}
          {s.store.recovery && (
            <Row
              tone={s.store.recovery.status === "running" && s.store.recovery.updated_at && now - new Date(s.store.recovery.updated_at).getTime() > 20 * 60000 ? "warn" : "ok"}
              title={`Recovery batch (${s.store.recovery.status})`}
              sub={`Started ${ago(s.store.recovery.started_at, now)} · updated ${ago(s.store.recovery.updated_at, now)}`}
              right={<span className="tabular-nums text-[13px]">{num(s.store.recovery.sent)} / {num(s.store.recovery.requested)} sent · {num(s.store.recovery.failed)} failed</span>}
              to="/apps/recovery"
            />
          )}
          {s.store.outboxes && (
            <Row
              tone={s.store.outboxes.klaviyo_pending_old || s.store.outboxes.conversions_pending_old ? "warn" : "ok"}
              title="Klaviyo and Meta syncs"
              sub="Updates waiting more than 30 minutes"
              right={<span className="tabular-nums text-[13px]">{s.store.outboxes.klaviyo_pending_old} Klaviyo · {s.store.outboxes.conversions_pending_old} Meta</span>}
            />
          )}
        </Card>
      </div>

      {Object.keys(s.errors).length > 0 && (
        <details className="rounded-[12px] border border-[var(--tb-line)] bg-[var(--tb-surface,#fff)] px-4 py-3 text-[12px] text-[var(--tb-ink-3)]">
          <summary className="cursor-pointer">Some checks couldn't load ({Object.keys(s.errors).length})</summary>
          <ul className="mt-2 space-y-1 font-mono text-[11px]">
            {Object.entries(s.errors).map(([k, v]) => (
              <li key={k} className="break-words">{k}: {v}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

const rank: Record<Tone, number> = { ok: 0, unknown: 1, warn: 2, down: 3 }
const worstOf = (ts: Tone[]): Tone => (ts.length ? ts.reduce((a, b) => (rank[b] > rank[a] ? b : a), "ok" as Tone) : "unknown")
const rateTone = (bad: number, good: number, limit: number, minTotal: number): Tone => (bad + good >= minTotal && bad / (bad + good) >= limit ? "warn" : "ok")

function Overall({ s }: { s: SystemStatus }) {
  const down = s.problems.filter((p) => p.tone === "down").length
  const title =
    s.overall === "ok" ? "All systems normal" : s.overall === "down" ? `${down} problem${down === 1 ? "" : "s"} need${down === 1 ? "s" : ""} action now` : s.overall === "warn" ? `${s.problems.length} thing${s.problems.length === 1 ? "" : "s"} to keep an eye on` : "Some checks couldn't load"
  const bg = s.overall === "ok" ? "bg-[color:var(--k-ok-bg-faint)] border-[color:var(--k-ok-line)]" : s.overall === "down" ? "bg-[color:var(--k-crit-bg-soft)] border-[color:var(--k-crit-line-soft)]" : s.overall === "warn" ? "bg-[color:var(--k-warn-bg-soft)] border-[color:var(--k-warn-line)]" : "bg-[var(--tb-surface,#fff)] border-[var(--tb-line)]"
  return (
    <div className={`rounded-[12px] border px-4 py-3 ${bg}`} role="status" aria-live="polite">
      <div className="flex items-center gap-x-2">
        <Dot tone={s.overall} size={10} />
        <p className="text-[15px] font-semibold text-[var(--tb-ink)]">{title}</p>
      </div>
      {s.problems.length > 0 && (
        <ul className="mt-2 flex flex-col gap-y-1.5">
          {s.problems.map((p, i) => (
            <ProblemLine key={i} p={p} />
          ))}
        </ul>
      )}
    </div>
  )
}

function ProblemLine({ p }: { p: Problem }) {
  const body = (
    <>
      <span className="mt-[5px] shrink-0"><Dot tone={p.tone} /></span>
      <span className="min-w-0">
        <span className="text-[13px] text-[var(--tb-ink)]">
          <span className="text-[var(--tb-ink-3)]">{p.area}: </span>
          {p.title}
        </span>
        {p.detail && <span className="block text-[12px] text-[var(--tb-ink-3)]">{p.detail}</span>}
      </span>
    </>
  )
  return <li>{p.link ? <Link to={p.link} className="flex gap-x-2 hover:underline">{body}</Link> : <div className="flex gap-x-2">{body}</div>}</li>
}

function Jobs({ jobs, now }: { jobs: JobRow[]; now: number }) {
  const [all, setAll] = useState(false)
  // Late only because the worker is being deployed: shown, not counted as a problem.
  const bad = jobs.filter((j) => !j.deploy_pause && (j.state === "late" || j.state === "failed"))
  const paused = jobs.filter((j) => j.deploy_pause)
  const unknown = jobs.filter((j) => j.state === "unknown").length
  const shown = all ? [...jobs].sort((a, b) => order(a) - order(b)) : [...bad, ...paused]
  const tone: Tone = bad.some((j) => j.state === "late" && j.critical) ? "down" : bad.length ? "warn" : unknown === jobs.length ? "unknown" : "ok"
  return (
    <Card
      title="Background jobs"
      tone={tone}
      count={`${jobs.length - bad.length - paused.length - unknown} of ${jobs.length} on time${paused.length ? ` · ${paused.length} paused for a deploy` : ""}`}
      actions={
        <button type="button" className="txt-compact-small text-ui-fg-interactive hover:underline" onClick={() => setAll((v) => !v)}>
          {all ? "Show problems only" : "Show all"}
        </button>
      }
    >
      {shown.length ? (
        shown.map((j) => (
          <Row
            key={j.name}
            tone={j.deploy_pause ? "unknown" : j.state === "late" ? (j.critical ? "down" : "warn") : j.state === "failed" ? "warn" : j.state}
            title={j.label}
            sub={j.deploy_pause ? `Paused while the background worker restarts for a deploy (no run for ${j.late_minutes} min)` : j.state === "failed" && j.error ? `Last run failed: ${j.error}` : j.state === "late" ? `Late: no run for ${j.late_minutes} min` : `${j.name} · ${j.schedule}`}
            right={<span className="text-[12px] tabular-nums text-[var(--tb-ink-3)]">{j.last_at ? `${ago(j.last_at, now)}${j.ms != null ? ` · ${(j.ms / 1000).toFixed(1)} s` : ""}` : "Waiting for first run"}</span>}
            compact
          />
        ))
      ) : (
        <Empty>{unknown === jobs.length ? "No runs recorded yet. Jobs report here after the next deploy." : "Every job ran on time."}</Empty>
      )}
    </Card>
  )
}
/** Firing alarms first; the OK ones fold into one line so a phone doesn't scroll past 15 rows. */
function Alarms({ alarms, now }: { alarms: SystemStatus["alarms"]; now: number }) {
  const [all, setAll] = useState(false)
  const firing = alarms.filter((a) => a.state === "ALARM")
  const rest = alarms.filter((a) => a.state !== "ALARM")
  const shown = all ? [...firing, ...rest] : firing
  return (
    <Card
      title="AWS alarms"
      tone={firing.length ? "down" : alarms.length ? "ok" : "unknown"}
      count={alarms.length ? `${firing.length} of ${alarms.length} firing` : undefined}
      actions={
        rest.length ? (
          <button type="button" className="txt-compact-small text-ui-fg-interactive hover:underline" onClick={() => setAll((v) => !v)}>
            {all ? "Firing only" : "Show all"}
          </button>
        ) : undefined
      }
    >
      {!alarms.length ? (
        <Empty>No alarms read.</Empty>
      ) : (
        <>
          {shown.map((a) => (
            <Row
              key={a.name}
              tone={a.state === "ALARM" ? "down" : a.state === "OK" ? "ok" : "unknown"}
              title={a.name}
              sub={a.state === "ALARM" ? a.description ?? a.reason ?? undefined : undefined}
              right={<span className="text-[12px] text-[var(--tb-ink-3)]">{a.state === "ALARM" ? `Firing ${ago(a.since, now)}` : a.state === "OK" ? "OK" : "No data"}</span>}
              compact
            />
          ))}
          {!all && !firing.length && <Empty>All {alarms.length} alarms are OK (payments, server errors, database, servers down, security changes).</Empty>}
        </>
      )}
    </Card>
  )
}

const SOURCE_LABEL: Record<string, string> = {
  backend: "API",
  job: "Background job",
  subscriber: "Event handler",
  "storefront-server": "Storefront server",
  "storefront-browser": "Shopper's browser",
  "admin-browser": "Admin in a browser",
}

/**
 * Unhandled errors of the last 24 hours, one row per kind of error (same type, message and place
 * in the code), newest first and spikes on top. "Logs" opens CloudWatch Logs Insights on the
 * last request that hit it; "All" on every occurrence. Backend: lib/observability/store.ts.
 */
function RecentErrorsCard({ r, failed, now }: { r: RecentErrors | null; failed: boolean; now: number }) {
  const [all, setAll] = useState(false)
  const groups = r?.groups ?? []
  const shown = all ? groups : groups.slice(0, 8)
  const hourAgo = now - 3600_000
  const tone: Tone = !r ? "unknown" : r.spiking ? "down" : groups.some((g) => new Date(g.last_seen_at).getTime() > hourAgo) ? "warn" : "ok"
  return (
    <div id="recent-errors" className="scroll-mt-20">
      <Card
        title="Recent errors (24 h)"
        tone={tone}
        count={r ? `${num(r.total_24h)} error${r.total_24h === 1 ? "" : "s"} · ${r.groups_24h} kind${r.groups_24h === 1 ? "" : "s"}${r.new_24h ? ` · ${r.new_24h} new` : ""}` : undefined}
        actions={
          groups.length > 8 ? (
            <button type="button" className="txt-compact-small text-ui-fg-interactive hover:underline" onClick={() => setAll((v) => !v)}>
              {all ? "Show fewer" : `Show all ${groups.length}`}
            </button>
          ) : undefined
        }
      >
        {!r ? (
          <Empty>{failed ? "Recent errors couldn't be read." : "No error data."}</Empty>
        ) : !groups.length ? (
          <Empty>No errors in the last 24 hours.</Empty>
        ) : (
          shown.map((g) => <ErrorRow key={g.id} g={g} now={now} />)
        )}
        {r?.noise?.total_24h ? <NoiseLine noise={r.noise} now={now} /> : null}
      </Card>
    </div>
  )
}

function ErrorRow({ g, now }: { g: ErrorGroup; now: number }) {
  const isNew = now - new Date(g.first_seen_at).getTime() < 24 * 3600_000
  const tone: Tone = g.spiking ? "down" : now - new Date(g.last_seen_at).getTime() < 3600_000 ? "warn" : "ok"
  return (
    <li>
      <div className="flex items-start justify-between gap-x-3 border-t border-[var(--tb-line)] px-4 py-2.5 first:border-t-0">
        <div className="flex min-w-0 items-start gap-x-2">
          <span className="mt-[6px] shrink-0"><Dot tone={tone} /></span>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-x-1.5">
              <span className="truncate text-[13px] text-[var(--tb-ink)]" title={`${g.kind}: ${g.message}`}>
                <span className="font-medium">{g.kind}</span>: {g.message}
              </span>
              {g.spiking ? <Badge tone="critical">Spike</Badge> : isNew ? <Badge tone="attention">New</Badge> : null}
            </div>
            <div className="truncate text-[12px] text-[var(--tb-ink-3)]" title={g.frames.join("\n")}>
              {SOURCE_LABEL[g.source] ?? g.source}
              {g.route ? ` · ${g.route}` : ""}
              {` · first ${ago(g.first_seen_at, now)} · last ${ago(g.last_seen_at, now)}`}
              {g.release ? ` · ${g.release}` : ""}
            </div>
            {g.path || g.browser || g.page_cache ? (
              <div className="truncate text-[12px] text-[var(--tb-ink-3)]">
                {[g.path ? `Last on ${g.path}` : null, g.browser, g.page_cache && g.page_cache !== "none" ? `shared page cache ${g.page_cache}` : g.page_cache === "none" ? "not from the page cache" : null, g.where ? `caught by ${g.where}` : null]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            ) : null}
            {g.component_stack ? (
              <details className="mt-0.5 text-[11px] text-[var(--tb-ink-3)]">
                <summary className="cursor-pointer select-none text-ui-fg-interactive">Component stack</summary>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px]">{g.component_stack}</pre>
              </details>
            ) : null}
            {g.last_request_id && (
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-[var(--tb-ink-3)]">
                <span>
                  Request <span className="select-all font-mono">{g.last_request_id}</span>
                </span>
                {g.logs_url && (
                  <a href={g.logs_url} target="_blank" rel="noreferrer noopener" className="text-ui-fg-interactive hover:underline">
                    Logs
                  </a>
                )}
                {g.fingerprint_logs_url && (
                  <a href={g.fingerprint_logs_url} target="_blank" rel="noreferrer noopener" className="text-ui-fg-interactive hover:underline">
                    All occurrences
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="tabular-nums text-[13px] text-[var(--tb-ink)]">{num(g.count_24h)}×</div>
          <div className="tabular-nums text-[11px] text-[var(--tb-ink-3)]">{num(g.count_total)} in all</div>
        </div>
      </div>
    </li>
  )
}

/**
 * Browser noise and deploy skew of the last 24 hours (backend lib/observability/noise.ts): network
 * drops, browser extensions, translated pages, tabs on an older build after a deploy. Counted so a
 * surge would show, never listed as errors.
 */
function NoiseLine({ noise, now }: { noise: NonNullable<RecentErrors["noise"]>; now: number }) {
  return (
    <div className="border-t border-[var(--tb-line)] px-4 py-2.5 text-[12px] text-[var(--tb-ink-3)]">
      <span className="text-[var(--tb-ink-2)]">Not counted as errors: {num(noise.total_24h)}</span>
      {" · "}
      {noise.buckets.map((b, i) => (
        <span key={b.reason} title={`Last ${ago(b.last_seen_at, now)}`}>
          {i > 0 ? " · " : ""}
          {b.label} {num(b.count_24h)}
        </span>
      ))}
    </div>
  )
}

/**
 * Checkout payments of the last hour (backend lib/payment-health.ts): card declines are the
 * shopper's bank saying no, shown by reason; failures on our side are what needs action.
 */
function PaymentRows({ p, problems }: { p: PaymentHealth; problems: Problem[] }) {
  const worst = (pred: (x: Problem) => boolean): Tone => (problems.filter(pred).some((x) => x.tone === "down") ? "down" : problems.some(pred) ? "warn" : "ok")
  const rate = p.decline_rate != null ? `${Math.round(p.decline_rate * 100)}% of checkouts` : null
  return (
    <>
      <Row
        tone={worst((x) => /failed on our side/.test(x.title))}
        title="Payments failed on our side"
        sub={p.system_failed ? p.system_by_reason.map((x) => `${x.reason} ${x.count}`).join(" · ") : "Gateway errors, timeouts, checkout errors"}
        right={<span className="tabular-nums text-[13px]">{num(p.system_failed)}</span>}
      />
      <Row
        tone={worst((x) => /declin/.test(x.title))}
        title="Card declines"
        sub={p.declined ? `${p.declines_by_reason.map((x) => `${x.reason} ${x.count}`).join(" · ")}${rate ? ` · ${p.checkouts_declined} of ${p.checkouts} checkouts (${rate}) ended declined` : ""}` : "Declines by the bank or address checks are normal; a high rate or 30+ is flagged"}
        right={<span className="tabular-nums text-[13px]">{num(p.declined)}</span>}
      />
      <Row tone="ok" title="Payments approved" sub={`${num(p.attempts)} payment attempt${p.attempts === 1 ? "" : "s"}${p.customer ? ` · ${p.customer} stopped before the bank (expired card details, limits)` : ""}`} right={<span className="tabular-nums text-[13px]">{num(p.approved)}</span>} />
    </>
  )
}

const order = (j: JobRow) => (j.deploy_pause ? 2 : j.state === "late" ? 0 : j.state === "failed" ? 1 : j.state === "unknown" ? 3 : 2)

function Card({ title, tone, count, actions, children }: { title: string; tone: Tone; count?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="tf-card overflow-hidden rounded-[12px] border border-[var(--tb-line)] bg-[var(--tb-surface,#fff)]">
      <header className="flex min-h-[44px] items-center justify-between gap-x-3 px-4 pb-1 pt-3">
        <div className="flex items-center gap-x-2">
          <h2 className="whitespace-nowrap text-[14px] font-semibold text-[var(--tb-ink)]">{title}</h2>
          <Badge tone={TONE[tone].badge}>{TONE[tone].label}</Badge>
        </div>
        <div className="flex items-center gap-x-3">
          {count && <span className="hidden text-[12px] text-[var(--tb-ink-3)] sm:inline">{count}</span>}
          {actions}
        </div>
      </header>
      <ul className="pb-1">{children}</ul>
    </section>
  )
}

function Row({ tone, title, sub, right, to, compact }: { tone: Tone; title: ReactNode; sub?: ReactNode; right?: ReactNode; to?: string; compact?: boolean }) {
  const inner = (
    <div className={`flex items-center justify-between gap-x-3 border-t border-[var(--tb-line)] px-4 ${compact ? "py-2" : "py-2.5"} first:border-t-0`}>
      <div className="flex min-w-0 items-start gap-x-2">
        <span className="mt-[6px] shrink-0"><Dot tone={tone} /></span>
        <div className="min-w-0">
          <div className="truncate text-[13px] text-[var(--tb-ink)]">{title}</div>
          {sub && <div className="truncate text-[12px] text-[var(--tb-ink-3)]">{sub}</div>}
        </div>
      </div>
      <div className="shrink-0">{right}</div>
    </div>
  )
  return <li>{to ? <Link to={to} className="block hover:bg-[var(--tb-surface-hover,#f7f7f7)]">{inner}</Link> : inner}</li>
}

function Tile({ label, value, tone, hint, to }: { label: string; value: ReactNode; tone: Tone; hint?: string; to?: string }) {
  const body = (
    <div className={`tf-card h-full rounded-[10px] border bg-[var(--tb-surface,#fff)] px-3 py-2.5 ${tone === "down" ? "border-[color:var(--k-crit-line)]" : tone === "warn" ? "border-[color:var(--k-warn-line)]" : "border-[var(--tb-line)]"}`}>
      <span className="flex items-center gap-x-1.5 text-[12px] text-[var(--tb-ink-3)]">
        <Dot tone={tone} />
        {label}
      </span>
      <span className="mt-0.5 block text-[20px] font-semibold tabular-nums text-[var(--tb-ink)]">{value}</span>
      {hint && <span className="block text-[11px] text-[var(--tb-ink-3)]">{hint}</span>}
    </div>
  )
  return to ? <Link to={to} className="block hover:opacity-90">{body}</Link> : body
}

const Dot = ({ tone, size = 8 }: { tone: Tone; size?: number }) => (
  <span aria-label={TONE[tone].label} className="inline-block rounded-full" style={{ width: size, height: size, background: TONE[tone].dot }} />
)

const Empty = ({ children }: { children: ReactNode }) => <li className="px-4 py-3 text-[13px] text-[var(--tb-ink-3)]">{children}</li>

/** Tiny last-hour line (1 point a minute). */
function Spark({ values, max }: { values: (number | null)[]; max?: number }) {
  const pts = values.map((v, i) => [i, v] as const).filter(([, v]) => v != null) as [number, number][]
  if (pts.length < 2) return <span className="inline-block w-[72px]" />
  const W = 72
  const H = 22
  const top = max ?? Math.max(1, ...pts.map(([, v]) => v))
  const n = Math.max(1, values.length - 1)
  const d = pts.map(([i, v], k) => `${k ? "L" : "M"}${((i / n) * W).toFixed(1)},${(H - 1 - (Math.min(v, top) / top) * (H - 2)).toFixed(1)}`).join(" ")
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" className="hidden sm:block">
      <path d={d} fill="none" stroke="var(--tb-ink-3, #8a8a8a)" strokeWidth="1.25" />
    </svg>
  )
}
